/**
 * SanctaMessageBlocks — inline UI blocks the LLM can embed in a reply
 * via lightweight XML-ish tags. Each tag renders a rich component INSIDE
 * the chat scroll — numbered product list, comparison, dupe finder,
 * SibaScore breakdown, cart preview.
 *
 * Parsing is a single-pass regex sweep: we split the assistant's
 * markdown into text + block segments, render each in order, then
 * animate them in via framer-motion so the whole reply feels like a
 * game-quality sales pitch.
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ShoppingBag, Loader2, Sparkles, Trophy, ArrowRight, Package } from "lucide-react";
import { api } from "@/lib/api";
import { productImage } from "@/lib/productImage";
import { SmartImage } from "@/components/shared/SmartImage";
import { SibaScoreBadge } from "@/components/shared/SibaScoreBadge";
import { useCurrency } from "@/contexts/CurrencyContext";

const BLOCK_TAGS = ["products", "compare", "dupe", "sibascore", "cart"] as const;
type BlockTag = (typeof BLOCK_TAGS)[number];

interface Segment {
  kind: "text" | "block";
  tag?: BlockTag;
  payload?: any;
  text?: string;
}

// Match <tag>{...}</tag> or <tag>...</tag> across newlines. Tolerant to
// stray whitespace so the LLM's imperfect JSON still renders.
const BLOCK_RE = /<(products|compare|dupe|sibascore|cart)>([\s\S]*?)<\/\1>/g;

export function parseSanctaMessage(raw: string): Segment[] {
  const out: Segment[] = [];
  let last = 0;
  const re = new RegExp(BLOCK_RE);
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    if (m.index > last) {
      out.push({ kind: "text", text: raw.slice(last, m.index) });
    }
    const tag = m[1] as BlockTag;
    let payload: any = null;
    const inner = (m[2] || "").trim();
    if (inner) {
      try { payload = JSON.parse(inner); } catch { payload = { _raw: inner }; }
    } else {
      payload = {};
    }
    out.push({ kind: "block", tag, payload });
    last = m.index + m[0].length;
  }
  if (last < raw.length) out.push({ kind: "text", text: raw.slice(last) });
  return out;
}

// ---- Renderer -------------------------------------------------------------
export const SanctaBlocks: React.FC<{
  segments: Segment[];
  conversationId?: string | null;
  renderText: (t: string) => React.ReactNode;
  onShortlistChange?: (slugs: string[]) => void;
}> = ({ segments, conversationId, renderText, onShortlistChange }) => {
  // Aggregate every product slug the LLM referenced this message and
  // publish it upward so the parent can persist the shortlist for
  // "add number 3"-style resolution on the next turn.
  React.useEffect(() => {
    const slugs: string[] = [];
    for (const s of segments) {
      if (s.kind !== "block") continue;
      const p = s.payload || {};
      if (Array.isArray(p.slugs)) slugs.push(...p.slugs);
      if (typeof p.slug === "string") slugs.push(p.slug);
    }
    if (slugs.length && onShortlistChange) onShortlistChange(slugs);
  }, [segments, onShortlistChange]);

  return (
    <>
      {segments.map((seg, i) => {
        if (seg.kind === "text") return <React.Fragment key={i}>{renderText(seg.text || "")}</React.Fragment>;
        const key = `${seg.tag}-${i}`;
        switch (seg.tag) {
          case "products": return <ProductShortlistBlock key={key} payload={seg.payload} startIndex={countSlugsBefore(segments, i)} />;
          case "compare":  return <CompareBlock key={key} payload={seg.payload} />;
          case "dupe":     return <DupeBlock key={key} payload={seg.payload} />;
          case "sibascore":return <SibaScoreBlock key={key} payload={seg.payload} />;
          case "cart":     return <CartPreviewBlock key={key} />;
          default: return null;
        }
      })}
    </>
  );
};

// Count product slugs that appeared before this block so numbering
// continues across multiple <products> blocks in the same message.
function countSlugsBefore(segments: Segment[], stopAt: number): number {
  let n = 0;
  for (let i = 0; i < stopAt; i++) {
    const s = segments[i];
    if (s.kind === "block" && s.tag === "products" && Array.isArray(s.payload?.slugs)) {
      n += s.payload.slugs.length;
    }
  }
  return n;
}

// ---- Product shortlist (numbered) ----------------------------------------
export const ProductShortlistBlock: React.FC<{ payload: any; startIndex?: number }> = ({ payload, startIndex = 0 }) => {
  const slugs: string[] = Array.isArray(payload?.slugs) ? payload.slugs : [];
  const q = useQuery<{ items: any[] }>({
    queryKey: ["sancta-products", slugs.join(",")],
    queryFn: async () => {
      const items = await Promise.all(
        slugs.map(async (s) => {
          try { return (await api.get(`/sancta/agent/product/${s}`)).data; }
          catch { return null; }
        })
      );
      return { items: items.filter(Boolean) };
    },
    enabled: slugs.length > 0,
  });

  if (!slugs.length) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="my-3 grid grid-cols-2 gap-2"
      data-testid="sancta-block-products"
    >
      {(q.data?.items || slugs.map(() => null)).map((p, i) => (
        <ProductCard
          key={p?.slug || i}
          number={startIndex + i + 1}
          p={p}
        />
      ))}
    </motion.div>
  );
};

const ProductCard: React.FC<{ number: number; p: any }> = ({ number, p }) => {
  const { formatPrice } = useCurrency();
  const qc = useQueryClient();
  const add = useMutation({
    mutationFn: async () => (await api.post("/marketplace/cart/items", { product_id: p.product_id, quantity: 1 })).data,
    onSuccess: () => { toast.success(`Added #${number} to cart`); qc.invalidateQueries({ queryKey: ["marketplace-cart"] }); },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Please log in first"),
  });
  if (!p) {
    return (
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 text-xs text-slate-400" data-testid={`sancta-product-${number}`}>
        Product #{number} unavailable
      </div>
    );
  }
  return (
    <motion.div
      whileHover={{ y: -2 }}
      className="relative rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
      data-testid={`sancta-product-${number}`}
    >
      <span className="absolute top-1.5 left-1.5 z-10 bg-slate-900 text-white text-[10px] font-bold h-5 min-w-[20px] px-1 rounded flex items-center justify-center tabular-nums">
        {number}
      </span>
      {p.product_score != null && (
        <span className="absolute top-1.5 right-1.5 z-10">
          <SibaScoreBadge score={p.product_score} size="xs" showLabel={false} />
        </span>
      )}
      <Link to={`/shop/p/${p.slug}`} className="block">
        <div className="aspect-square bg-slate-50 dark:bg-slate-800 relative overflow-hidden">
          <SmartImage src={productImage(p, 400)} alt={p.title} wrapperClassName="absolute inset-0" className="h-full w-full object-cover" />
        </div>
      </Link>
      <div className="p-2">
        <div className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wider font-semibold truncate">{p.brand || "SociBaba"}</div>
        <Link to={`/shop/p/${p.slug}`} className="block text-xs font-semibold text-slate-900 dark:text-white line-clamp-2 min-h-[2em] hover:text-orange-500">
          {p.title}
        </Link>
        <div className="mt-1.5 flex items-center justify-between gap-1">
          <div className="font-display font-bold text-sm text-slate-900 dark:text-white tabular-nums">{formatPrice(p.price || 0)}</div>
          <button
            type="button"
            onClick={() => add.mutate()}
            disabled={add.isPending}
            className="inline-flex items-center gap-0.5 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-[10px] font-bold px-2 py-1 hover:opacity-90 disabled:opacity-50"
            data-testid={`sancta-product-${number}-add`}
          >
            {add.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <><ShoppingBag className="h-3 w-3" /> Add</>}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ---- Comparison ----------------------------------------------------------
export const CompareBlock: React.FC<{ payload: any }> = ({ payload }) => {
  const slugs: string[] = Array.isArray(payload?.slugs) ? payload.slugs : [];
  const { formatPrice } = useCurrency();
  const q = useQuery<any>({
    queryKey: ["sancta-compare", slugs.join(",")],
    queryFn: async () => (await api.post("/sancta/agent/compare", { slugs })).data,
    enabled: slugs.length >= 2,
  });
  if (!slugs.length) return null;
  const products = q.data?.products || [];
  const winner = q.data?.winner_slug;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.35 }}
      className="my-3 rounded-2xl border border-fuchsia-200 dark:border-fuchsia-800/40 bg-gradient-to-br from-fuchsia-50 via-white to-amber-50 dark:from-fuchsia-950/30 dark:via-slate-900 dark:to-amber-950/20 p-3"
      data-testid="sancta-block-compare"
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-fuchsia-700 dark:text-fuchsia-300 mb-2">
        <Trophy className="h-3 w-3" /> Head-to-head
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {products.map((p: any) => (
          <div
            key={p.slug}
            className={`relative rounded-xl border p-2 bg-white dark:bg-slate-900 ${
              p.slug === winner ? "border-amber-400 shadow-sm shadow-amber-400/30" : "border-slate-200 dark:border-slate-800"
            }`}
            data-testid={`sancta-compare-tile-${p.slug}`}
          >
            {p.slug === winner && (
              <span className="absolute -top-2 left-2 bg-amber-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-widest">
                Pick
              </span>
            )}
            <div className="aspect-square bg-slate-50 dark:bg-slate-800 rounded-lg overflow-hidden mb-1.5 relative">
              <SmartImage src={productImage(p, 320)} alt={p.title} wrapperClassName="absolute inset-0" className="h-full w-full object-cover" />
            </div>
            <div className="text-[11px] font-semibold text-slate-900 dark:text-white line-clamp-2 min-h-[2em]">{p.title}</div>
            <div className="mt-1 flex items-center justify-between text-[10px]">
              <span className="tabular-nums font-bold">{formatPrice(p.price || 0)}</span>
              {p.product_score != null && <SibaScoreBadge score={p.product_score} size="xs" showLabel={false} />}
            </div>
          </div>
        ))}
      </div>
      {q.data?.reason && (
        <div className="mt-2 text-xs text-slate-700 dark:text-slate-300">
          <span className="font-semibold">Why: </span>{q.data.reason}
        </div>
      )}
    </motion.div>
  );
};

// ---- Dupe Finder ---------------------------------------------------------
export const DupeBlock: React.FC<{ payload: any }> = ({ payload }) => {
  const slug: string = payload?.slug;
  const { formatPrice } = useCurrency();
  const q = useQuery<any>({
    queryKey: ["sancta-dupe", slug],
    queryFn: async () => (await api.get(`/sancta/agent/dupe-finder/${slug}`)).data,
    enabled: !!slug,
  });
  if (!slug) return null;
  const matches = q.data?.matches || [];
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="my-3 rounded-2xl border border-emerald-200 dark:border-emerald-800/40 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/20 dark:to-slate-900 p-3"
      data-testid="sancta-block-dupe"
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-emerald-700 dark:text-emerald-400 mb-2">
        <Sparkles className="h-3 w-3" /> Cheaper matches
      </div>
      {matches.length === 0 ? (
        <div className="text-xs text-slate-500">No cheaper alternatives found in the same category.</div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {matches.map((m: any) => (
            <Link
              key={m.slug}
              to={`/shop/p/${m.slug}`}
              className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-1.5 hover:shadow-md transition-shadow"
              data-testid={`sancta-dupe-tile-${m.slug}`}
            >
              <div className="aspect-square bg-slate-50 dark:bg-slate-800 rounded overflow-hidden mb-1 relative">
                <SmartImage src={productImage(m, 240)} alt={m.title} wrapperClassName="absolute inset-0" className="h-full w-full object-cover" />
              </div>
              <div className="text-[10px] font-semibold line-clamp-2 min-h-[2em] text-slate-900 dark:text-white">{m.title}</div>
              <div className="flex items-center justify-between mt-0.5">
                <span className="text-[10px] font-bold tabular-nums">{formatPrice(m.price || 0)}</span>
                {m.savings_pct != null && (
                  <span className="text-[9px] font-bold text-emerald-600 dark:text-emerald-400">-{m.savings_pct}%</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </motion.div>
  );
};

// ---- SibaScore breakdown -------------------------------------------------
export const SibaScoreBlock: React.FC<{ payload: any }> = ({ payload }) => {
  const slug: string = payload?.slug;
  const q = useQuery<any>({
    queryKey: ["sancta-sibascore", slug],
    queryFn: async () => (await api.get(`/sancta/agent/sibascore/${slug}`)).data,
    enabled: !!slug,
  });
  if (!slug || !q.data) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="my-3 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3"
      data-testid="sancta-block-sibascore"
    >
      <div className="flex items-center gap-2 mb-2">
        <SibaScoreBadge score={q.data.master_score} size="sm" />
        <div className="text-xs font-bold text-slate-900 dark:text-white truncate">{q.data.title}</div>
      </div>
      <div className="space-y-1.5">
        {(q.data.parameters || []).map((p: any) => (
          <div key={p.key} data-testid={`sancta-sibascore-param-${p.key}`}>
            <div className="flex items-center justify-between text-[11px]">
              <span className="font-semibold text-slate-900 dark:text-white">{p.label}</span>
              <span className="tabular-nums font-mono text-slate-600 dark:text-slate-400">{p.score}/1000</span>
            </div>
            <div className="h-1 bg-slate-100 dark:bg-slate-800 rounded overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(p.score / 1000) * 100}%` }}
                transition={{ duration: 0.6 }}
                className="h-full bg-gradient-to-r from-orange-500 via-fuchsia-500 to-cyan-500"
              />
            </div>
            <div className="text-[9px] text-slate-500 dark:text-slate-400 mt-0.5">{p.description}</div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

// ---- Cart preview --------------------------------------------------------
export const CartPreviewBlock: React.FC = () => {
  const { formatPrice } = useCurrency();
  const q = useQuery<any>({
    queryKey: ["sancta-cart-preview"],
    queryFn: async () => (await api.get("/sancta/agent/cart-preview")).data,
    refetchInterval: 5000,
  });
  const d = q.data;
  if (!d) return null;
  if (!d.signed_in) {
    return (
      <div className="my-3 rounded-xl border border-amber-200 dark:border-amber-800/40 bg-amber-50 dark:bg-amber-950/20 p-2 text-xs text-amber-800 dark:text-amber-300" data-testid="sancta-block-cart">
        Log in to see your live cart total.
      </div>
    );
  }
  if (!d.count) {
    return (
      <div className="my-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-2 text-xs text-slate-500" data-testid="sancta-block-cart">
        <Package className="h-3 w-3 inline mr-1" /> Cart is empty.
      </div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="my-3 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3"
      data-testid="sancta-block-cart"
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-slate-400 mb-2">
        <ShoppingBag className="h-3 w-3" /> Your cart · {d.count} item{d.count === 1 ? "" : "s"}
      </div>
      <div className="space-y-1 mb-2">
        {(d.items || []).slice(0, 4).map((it: any) => (
          <div key={it.product_id} className="flex justify-between text-[11px] gap-2">
            <span className="text-slate-700 dark:text-slate-300 truncate">{it.quantity}× {it.title}</span>
            <span className="tabular-nums font-mono">{formatPrice((it.price || 0) * (it.quantity || 1))}</span>
          </div>
        ))}
        {(d.items || []).length > 4 && (
          <div className="text-[10px] italic text-slate-500">+ {d.items.length - 4} more</div>
        )}
      </div>
      <div className="border-t border-slate-100 dark:border-slate-800 pt-1.5 space-y-0.5 text-[11px]">
        <div className="flex justify-between text-slate-600 dark:text-slate-400"><span>Subtotal</span><span className="tabular-nums">{formatPrice(d.subtotal)}</span></div>
        <div className="flex justify-between text-slate-600 dark:text-slate-400"><span>Tax</span><span className="tabular-nums">{formatPrice(d.tax)}</span></div>
        <div className="flex justify-between text-slate-600 dark:text-slate-400"><span>Shipping</span><span className="tabular-nums">{formatPrice(d.shipping)}</span></div>
        <div className="flex justify-between font-bold text-sm pt-1 text-slate-900 dark:text-white"><span>Total</span><span className="tabular-nums">{formatPrice(d.total)}</span></div>
      </div>
      <Link
        to="/shop/cart"
        className="mt-2 inline-flex items-center gap-1 text-[11px] text-orange-600 dark:text-orange-400 font-semibold hover:underline"
      >
        Open cart <ArrowRight className="h-3 w-3" />
      </Link>
    </motion.div>
  );
};
